import fs from 'fs'
import path from 'path'

import type { Template } from './manifest'
import { TemplateManifestSchema } from './manifest'
import { templatesRoot } from './package-root'

// Templates are discovered from `templates/*/template.json`, so adding one is still just dropping in a
// folder. Which of them is the *default* is decided here, by a single id — deliberately not a `default:
// true` flag on each manifest, because two manifests could both set that and the winner would come down to
// directory order.

const MANIFEST_FILE = 'template.json'

/** The template `flowrunner cs demo` resolves to, and the one that heads the interactive picker. */
export const DEFAULT_TEMPLATE_ID = 'demo-todo-list'

/**
 * A stable shorthand for "whichever template is the default one", so `flowrunner cs demo` keeps working
 * if the default moves. The default template's own id still resolves normally.
 */
export const DEFAULT_TEMPLATE_ALIAS = 'demo'

// A manifest is checked the moment it is read, not when a file is finally written: a template that declares
// a missing source or a `to` path escaping the service directory is broken for everyone, and finding that
// out halfway through a render leaves the target project in a state the user has to clean up by hand.
function loadTemplate(directory: string): Template {
  const manifestPath = path.join(directory, MANIFEST_FILE)

  let raw: unknown

  try {
    raw = JSON.parse(fs.readFileSync(manifestPath, 'utf-8'))
  } catch (error) {
    throw new Error(`Template manifest ${ manifestPath } is not readable JSON: ${ (error as Error).message }`)
  }

  const parsed = TemplateManifestSchema.safeParse(raw)

  if (!parsed.success) {
    const issues = parsed.error.issues
      .map(issue => `  ${ issue.path.join('.') || '<root>' }: ${ issue.message }`)
      .join('\n')

    throw new Error(`Template manifest ${ manifestPath } is invalid:\n${ issues }`)
  }

  const manifest = parsed.data

  // The folder is how a template is found; the manifest id is how it is addressed. A mismatch would make
  // `cs <folder>` fail while the template looks perfectly present on disk.
  if (manifest.id !== path.basename(directory)) {
    throw new Error(`Template in ${ directory } declares id "${ manifest.id }" — it must match the folder name`)
  }

  for (const prompt of manifest.prompts) {
    if (prompt.type === 'choice' && !prompt.options.some(option => option.value === prompt.default)) {
      throw new Error(`Template "${ manifest.id }" prompt "${ prompt.name }" defaults to "${ prompt.default }", ` +
        `which is not one of its options (${ prompt.options.map(option => option.value).join(', ') })`)
    }
  }

  const filesRoot = path.join(directory, 'files')

  for (const file of manifest.files) {
    const source = path.resolve(filesRoot, file.from)

    if (!source.startsWith(filesRoot + path.sep)) {
      throw new Error(`Template "${ manifest.id }" declares a source outside files/: ${ file.from }`)
    }

    if (!fs.existsSync(source)) {
      throw new Error(`Template "${ manifest.id }" declares a missing source file: files/${ file.from }`)
    }

    // The real destination is per-service, but whether it can escape is decided by the manifest alone.
    const destination = path.normalize(file.to)

    if (path.isAbsolute(destination) || destination.split(/[\\/]/).includes('..')) {
      throw new Error(`Template "${ manifest.id }" declares a destination outside the service folder: ${ file.to }`)
    }

    const gate = file.when &&
      (manifest.prompts.some(prompt => prompt.type === 'boolean' && prompt.name === file.when) ||
        typeof manifest.values[file.when] === 'boolean')

    if (file.when && !gate) {
      throw new Error(`Template "${ manifest.id }" gates files/${ file.from } on "${ file.when }", ` +
        'which is neither a boolean prompt nor a boolean entry in `values`')
    }
  }

  return { manifest, directory, isDefault: manifest.id === DEFAULT_TEMPLATE_ID }
}

/** Every template shipped with the CLI — the default one first, then alphabetically. */
export function listTemplates(): Template[] {
  const root = templatesRoot()

  if (!fs.existsSync(root)) {
    return []
  }

  const templates = fs
    .readdirSync(root, { withFileTypes: true })
    .filter(entry => entry.isDirectory() && fs.existsSync(path.join(root, entry.name, MANIFEST_FILE)))
    .map(entry => loadTemplate(path.join(root, entry.name)))

  // Catches a rename or a deletion of the default template, rather than quietly leaving `cs demo` broken.
  if (templates.length > 0 && !templates.some(template => template.isDefault)) {
    throw new Error(`DEFAULT_TEMPLATE_ID is "${ DEFAULT_TEMPLATE_ID }", but no template with that id is bundled`)
  }

  return templates.sort((a, b) => Number(b.isDefault) - Number(a.isDefault) ||
    a.manifest.id.localeCompare(b.manifest.id))
}

export function defaultTemplate(): Template | null {
  return listTemplates().find(template => template.isDefault) ?? null
}

/** Resolves a template id, or the `demo` alias, to a template. */
export function findTemplate(id: string): Template | null {
  const templates = listTemplates()
  const match = templates.find(template => template.manifest.id === id)

  if (match) {
    return match
  }

  return id === DEFAULT_TEMPLATE_ALIAS
    ? templates.find(template => template.isDefault) ?? null
    : null
}
